import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, MenuItem,
    Chip, LinearProgress, Button, Paper, Avatar, Tabs, Tab, InputAdornment
} from '@mui/material';
import {
    Assessment as AssessmentIcon, Search as SearchIcon,
    CheckCircle as CheckIcon, Warning as WarningIcon,
    Schedule as ScheduleIcon, Download as DownloadIcon,
    Refresh as RefreshIcon, TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-toastify';

const ExamMonitoring = () => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState(0);
    const [classFilter, setClassFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => { fetchStats(); }, []);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const res = await api.get('/exams/stats');
            setStats(res.data.data || []);
        } catch (error) {
            console.error("Failed to fetch stats");
        } finally {
            setLoading(false);
        }
    };

    // Sample data for full UI
    const monitoringData = [
        { id: 1, exam: 'Unit Test 1', class: 'Class 10A', subject: 'Mathematics', totalStudents: 45, marksEntered: 45, evaluated: true, avgMarks: 72, passRate: 89, teacher: 'Mr. Patil' },
        { id: 2, exam: 'Unit Test 1', class: 'Class 10A', subject: 'Science', totalStudents: 45, marksEntered: 38, evaluated: false, avgMarks: 0, passRate: 0, teacher: 'Mrs. Sharma' },
        { id: 3, exam: 'Unit Test 1', class: 'Class 10B', subject: 'Mathematics', totalStudents: 42, marksEntered: 42, evaluated: true, avgMarks: 68, passRate: 83, teacher: 'Mr. Patil' },
        { id: 4, exam: 'Unit Test 1', class: 'Class 9A', subject: 'English', totalStudents: 40, marksEntered: 40, evaluated: true, avgMarks: 75, passRate: 92, teacher: 'Ms. Desai' },
        { id: 5, exam: 'Unit Test 1', class: 'Class 9A', subject: 'Hindi', totalStudents: 40, marksEntered: 20, evaluated: false, avgMarks: 0, passRate: 0, teacher: 'Mr. Kumar' },
        { id: 6, exam: 'Unit Test 1', class: 'Class 8A', subject: 'Science', totalStudents: 38, marksEntered: 0, evaluated: false, avgMarks: 0, passRate: 0, teacher: 'Mrs. Joshi' },
    ];

    const totalExams = monitoringData.length;
    const completed = monitoringData.filter(d => d.evaluated).length;
    const pending = totalExams - completed;
    const avgProgress = Math.round(monitoringData.reduce((a, d) => a + (d.marksEntered / d.totalStudents) * 100, 0) / totalExams);

    const filteredData = monitoringData.filter(d => {
        const matchTab = tab === 0 ? true : tab === 1 ? d.evaluated : !d.evaluated;
        const matchClass = !classFilter || d.class === classFilter;
        const matchSearch = !searchTerm || d.subject.toLowerCase().includes(searchTerm.toLowerCase()) || d.teacher.toLowerCase().includes(searchTerm.toLowerCase());
        return matchTab && matchClass && matchSearch;
    });

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>Exam Monitoring</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} size="small" onClick={fetchStats}>Refresh</Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} size="small">Export Report</Button>
                </Box>
            </Box>

            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <AssessmentIcon sx={{ color: '#1a9e8f', fontSize: 28, mb: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">Total Subjects</Typography>
                        <Typography variant="h4" fontWeight={700}>{totalExams}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <CheckIcon sx={{ color: '#4caf50', fontSize: 28, mb: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">Completed</Typography>
                        <Typography variant="h4" fontWeight={700} color="success.main">{completed}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <WarningIcon sx={{ color: '#ff9800', fontSize: 28, mb: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">Pending</Typography>
                        <Typography variant="h4" fontWeight={700} color="warning.main">{pending}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <TrendingUpIcon sx={{ color: '#2196f3', fontSize: 28, mb: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">Avg Progress</Typography>
                        <Typography variant="h4" fontWeight={700}>{avgProgress}%</Typography>
                    </CardContent></Card>
                </Grid>
            </Grid>

            <Card>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tab label={`All (${totalExams})`} />
                    <Tab label={`Completed (${completed})`} />
                    <Tab label={`Pending (${pending})`} />
                </Tabs>
                <Box sx={{ p: 2 }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth placeholder="Search subject or teacher..." value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField fullWidth select label="Class" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
                                <MenuItem value="">All Classes</MenuItem>
                                {[...new Set(monitoringData.map(d => d.class))].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                        </Grid>
                    </Grid>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Class</TableCell>
                                    <TableCell>Subject</TableCell>
                                    <TableCell>Teacher</TableCell>
                                    <TableCell align="center">Total</TableCell>
                                    <TableCell align="center">Entered</TableCell>
                                    <TableCell>Progress</TableCell>
                                    <TableCell align="center">Avg Marks</TableCell>
                                    <TableCell align="center">Pass %</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.map(d => {
                                    const progress = Math.round((d.marksEntered / d.totalStudents) * 100);
                                    return (
                                        <TableRow key={d.id} hover>
                                            <TableCell sx={{ fontWeight: 600 }}>{d.class}</TableCell>
                                            <TableCell>{d.subject}</TableCell>
                                            <TableCell>{d.teacher}</TableCell>
                                            <TableCell align="center">{d.totalStudents}</TableCell>
                                            <TableCell align="center">{d.marksEntered}</TableCell>
                                            <TableCell sx={{ minWidth: 120 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LinearProgress variant="determinate" value={progress}
                                                        sx={{
                                                            flexGrow: 1, height: 6, borderRadius: 3, bgcolor: '#e0e0e0',
                                                            '& .MuiLinearProgress-bar': { bgcolor: progress === 100 ? '#4caf50' : progress > 50 ? '#ff9800' : '#e53935' }
                                                        }} />
                                                    <Typography variant="caption" sx={{ minWidth: 30 }}>{progress}%</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>{d.evaluated ? d.avgMarks : '-'}</TableCell>
                                            <TableCell align="center">{d.evaluated ? `${d.passRate}%` : '-'}</TableCell>
                                            <TableCell>
                                                <Chip size="small" icon={d.evaluated ? <CheckIcon /> : <ScheduleIcon />}
                                                    label={d.evaluated ? 'Complete' : progress > 0 ? 'In Progress' : 'Not Started'}
                                                    color={d.evaluated ? 'success' : progress > 0 ? 'warning' : 'default'} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Card>
        </Box>
    );
};

export default ExamMonitoring;
